import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Navigation, MapPin, Home, Briefcase, Star, Share2, Calendar as CalendarIcon, Trash2, X, Plus, Route, Locate } from "lucide-react";
import { api } from "../api";
import { GEO } from "../constants/testIds";

// Approved palette only
const C = {
  bg: "#0a2e0a", card: "#0f3d0f", mosaic1: "#1a5c1a", mosaic2: "#4a9a4a",
  high1: "#CEF0CE", high2: "#DFF2DF", white: "#ffffff",
};

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = { lat: 1.3521, lng: 103.8198 }; // Singapore fallback

// Tiny HTML escaper — InfoWindow content is HTML, so any user/DB-supplied
// place fields must be sanitised to prevent broken markup.
const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// ─── Google Maps JS loader (single-shot promise) ─────────────────────────
//
// Robust against three pitfalls we hit in production:
//   1. `loading=async` defers sub-module init — `google.maps.ControlPosition`
//      can be undefined at the instant `script.onload` fires. We DROP that
//      flag and instead use Google's official `callback=` param, which is
//      only invoked once the maps namespace is fully populated.
//   2. StrictMode / HMR can re-invoke the effect and re-import this module.
//      We look for a pre-existing `<script data-dnya-gmaps="1">` in the DOM
//      and attach to its load event instead of double-injecting.
//   3. We resolve ONLY when `window.google.maps.ControlPosition` exists — so
//      downstream `new google.maps.Map(..., { position: ...RIGHT_TOP })`
//      calls cannot race the loader.
let _mapsPromise = null;
function loadGoogleMaps() {
  if (window.google?.maps?.ControlPosition) return Promise.resolve(window.google);
  if (_mapsPromise) return _mapsPromise;

  _mapsPromise = new Promise((resolve, reject) => {
    if (!GMAPS_KEY) return reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY not set"));

    const CB = "__dnyaGmapsInit__";
    const finish = () => {
      if (window.google?.maps?.ControlPosition) resolve(window.google);
      else reject(new Error("Google Maps loaded but the maps namespace is incomplete"));
    };
    // Google will call this exact global once the library is ready
    window[CB] = finish;

    // Reuse a script tag if one is already in flight (StrictMode double-effect)
    const existing = document.querySelector('script[data-dnya-gmaps="1"]');
    if (existing) {
      if (window.google?.maps?.ControlPosition) return finish();
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps SDK")), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(GMAPS_KEY)}` +
      "&libraries=geometry" +
      "&v=weekly" +
      `&callback=${CB}`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-dnya-gmaps", "1");
    s.onerror = () => reject(new Error("Failed to load Google Maps SDK"));
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

// Custom map style — dark green, minimal, palette-compliant
const DARK_GREEN_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0a2e0a" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a2e0a" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#CEF0CE" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#1a5c1a" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a5c1a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#0f3d0f" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#DFF2DF" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f3d0f" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a9a4a" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#0f3d0f" }] },
];

export default function Geolocation() {
  const loc = useLocation();
  const nav = useNavigate();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerObjectsRef = useRef([]);      // google.maps.Marker[]
  const infoWindowsRef = useRef([]);        // google.maps.InfoWindow[] — flat list, kept 1:1 with markerObjectsRef
  const pinLookupRef = useRef(new Map());   // place_key → { marker, infoWindow } — used by list-item click
  const openInfoRef = useRef(null);         // currently open InfoWindow (max 1)
  const directionsRendererRef = useRef(null); // Step 2: single re-used DirectionsRenderer
  const selectedMarkerRef = useRef(null);
  const meMarkerRef = useRef(null);
  const meCircleRef = useRef(null);
  const routePolylineRef = useRef(null); // legacy — retained for backward-compat with old tests
  const watchIdRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [me, setMe] = useState(null); // {lat,lng,accuracy}
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // place object
  const [favorites, setFavorites] = useState([]);
  const [directions, setDirections] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null); // Step 2: top-of-map banner { name, distance, duration }
  const [mode, setMode] = useState("driving");
  const [busy, setBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [showLabelPrompt, setShowLabelPrompt] = useState(false);
  // Floating search bubble (bottom-right of map): collapsed pill ↔ expanded input
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const [bubbleQuery, setBubbleQuery] = useState("");
  const bubbleInputRef = useRef(null);

  // ─── Init map ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled || !mapDivRef.current) return;
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center: DEFAULT_CENTER, zoom: 13,
        styles: DARK_GREEN_STYLE,
        disableDefaultUI: true,
        gestureHandling: "greedy",
        zoomControl: true, zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
      });
      // Step 2: one DirectionsRenderer, re-used for every route. suppressMarkers
      // ensures the built-in green/red pins do NOT hide our custom Step-1 pins.
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        map: mapRef.current,
        suppressMarkers: true,
        preserveViewport: false,
        polylineOptions: {
          strokeColor: C.mosaic1,      // #1a5c1a — palette-compliant
          strokeOpacity: 0.9,
          strokeWeight: 5,
        },
      });
      setMapReady(true);
    }).catch((e) => {
      console.error(e);
      setLoadError("Could not load the map. Check that the Google Maps JavaScript API is enabled and the API key allows this domain.");
    });
    return () => { cancelled = true; };
  }, []);

  // ─── Live blue dot ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setMe({ lat, lng, accuracy });
      },
      () => { /* silent — user may deny */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // Render/update the blue-dot marker whenever `me` changes
  useEffect(() => {
    if (!mapReady || !me || !window.google) return;
    const google = window.google;
    const pos = { lat: me.lat, lng: me.lng };
    if (!meMarkerRef.current) {
      meMarkerRef.current = new google.maps.Marker({
        position: pos, map: mapRef.current, zIndex: 999,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: C.high1, fillOpacity: 1,
          strokeColor: C.white, strokeWeight: 3,
        },
      });
      meCircleRef.current = new google.maps.Circle({
        map: mapRef.current, center: pos, radius: me.accuracy || 40,
        strokeColor: C.high1, strokeOpacity: 0.5, strokeWeight: 1,
        fillColor: C.high1, fillOpacity: 0.12,
      });
    } else {
      meMarkerRef.current.setPosition(pos);
      meCircleRef.current.setCenter(pos);
      if (me.accuracy) meCircleRef.current.setRadius(me.accuracy);
    }
  }, [mapReady, me]);

  // ─── Favorites: load once ───────────────────────────────────────────────
  const loadFavorites = useCallback(async () => {
    try {
      const r = await api.post("/geolocation/favorites", { action: "list" });
      setFavorites(r.data.favorites || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  // ─── Route clear (Step 2) ──────────────────────────────────────────────
  // Defined early so runSearch (which calls it) can list it in its deps.
  const clearRoute = useCallback(() => {
    if (directionsRendererRef.current) {
      try { directionsRendererRef.current.setDirections({ routes: [] }); }
      catch (_) { /* ignore */ }
    }
    setRouteInfo(null);
    setDirections(null);
  }, []);

  // ─── Result-pin plotting ───────────────────────────────────────────────
  const clearMarkers = useCallback(() => {
    // Detach all markers from the map
    markerObjectsRef.current.forEach((m) => {
      try { m.setMap(null); } catch (_) { /* ignore */ }
    });
    // Close all InfoWindows — each is a real google.maps.InfoWindow, so .close() is safe
    infoWindowsRef.current.forEach((iw) => {
      try { iw.close(); } catch (_) { /* ignore */ }
    });
    markerObjectsRef.current = [];
    infoWindowsRef.current = [];
    pinLookupRef.current.clear();
    if (openInfoRef.current) {
      try { openInfoRef.current.close(); } catch (_) { /* ignore */ }
      openInfoRef.current = null;
    }
  }, []);

  const _placeKey = (p) => p.place_id || `${p.latitude},${p.longitude}`;

  const plotResults = useCallback((items) => {
    clearMarkers();
    if (!mapRef.current || !window.google || !items?.length) return;
    const google = window.google;
    const bounds = new google.maps.LatLngBounds();

    items.forEach((place) => {
      const lat = parseFloat(place.latitude);
      const lng = parseFloat(place.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const position = { lat, lng };

      const marker = new google.maps.Marker({
        position, map: mapRef.current, title: place.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: C.mosaic1,       // #1a5c1a
          fillOpacity: 1,
          strokeColor: C.high1,       // #CEF0CE
          strokeWeight: 2,
        },
      });

      const distanceText = place.distance_text || (place.distance_km != null ? `${place.distance_km.toFixed(1)} km` : "");
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="color:#0a2e0a;padding:4px;font-family:Inter,system-ui,sans-serif;min-width:170px;max-width:220px">
            <div style="font-weight:600;font-size:14px;margin-bottom:4px;color:#0a2e0a">${escapeHtml(place.name || "Place")}</div>
            <div style="font-size:12px;color:#4a9a4a;line-height:1.35">${escapeHtml(place.address || "")}</div>
            ${distanceText ? `<div style="font-size:12px;color:#1a5c1a;margin-top:4px;font-weight:500">${escapeHtml(distanceText)}</div>` : ""}
          </div>`,
      });

      marker.addListener("click", () => {
        if (openInfoRef.current && openInfoRef.current !== infoWindow) openInfoRef.current.close();
        infoWindow.open(mapRef.current, marker);
        openInfoRef.current = infoWindow;
        selectPlace(place);
      });

      // Store markers and InfoWindows in flat parallel arrays — clearMarkers()
      // iterates each with the correct API (.setMap(null) / .close()).
      markerObjectsRef.current.push(marker);
      infoWindowsRef.current.push(infoWindow);
      // Separate lookup Map so a click on a results-list row can find the
      // right pin + InfoWindow to open on the map.
      pinLookupRef.current.set(_placeKey(place), { marker, infoWindow });
      bounds.extend(position);
    });

    if (me) bounds.extend({ lat: me.lat, lng: me.lng });

    if (markerObjectsRef.current.length === 1 && !me) {
      mapRef.current.setCenter(markerObjectsRef.current[0].getPosition());
      mapRef.current.setZoom(15);
    } else if (markerObjectsRef.current.length >= 1) {
      mapRef.current.fitBounds(bounds, 80);
    }
  }, [clearMarkers, me]);

  // ─── Search ─────────────────────────────────────────────────────────────
  const runSearch = useCallback(async (q, options = {}) => {
    const query = (q || "").trim();
    if (!query) return;
    setBusy(true); setSheetOpen(true);
    // Step 2: any new search clears the previous route so it doesn't linger
    // over the fresh set of pins.
    clearRoute();
    try {
      const body = { query, max_results: 8 };
      if (me) { body.lat = me.lat; body.lng = me.lng; }
      const r = await api.post("/geolocation/search", body);
      const items = r.data.results || [];
      setResults(items);
      plotResults(items);
      if (items.length === 0) toast("No matching places found");
      else if (options.autoSelect && items.length === 1) selectPlace(items[0]);
      else if (options.autoSelect && items.length > 1) {
        // Voice-driven multi-result: still auto-pick first, but the sheet shows all
        selectPlace(items[0]);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Search failed";
      toast.error(msg);
      setLoadError(msg);
      setResults([]);
      plotResults([]);
    } finally {
      setBusy(false);
    }
  }, [me, plotResults, clearRoute]);

  // ─── Select / focus a place ────────────────────────────────────────────
  const selectPlace = useCallback((place) => {
    setSelected(place);
    setDirections(null);
    if (!mapRef.current || !window.google || place.latitude == null || place.longitude == null) return;
    const google = window.google;
    const pos = { lat: place.latitude, lng: place.longitude };
    if (selectedMarkerRef.current) selectedMarkerRef.current.setMap(null);
    selectedMarkerRef.current = new google.maps.Marker({
      position: pos, map: mapRef.current,
      icon: {
        path: "M 0,-22 C -8,-22 -14,-16 -14,-8 C -14,2 0,22 0,22 C 0,22 14,2 14,-8 C 14,-16 8,-22 0,-22 Z",
        fillColor: C.mosaic1, fillOpacity: 1,
        strokeColor: C.high1, strokeWeight: 2,
        anchor: new google.maps.Point(0, 0),
      },
    });
    mapRef.current.panTo(pos);
    if (mapRef.current.getZoom() < 14) mapRef.current.setZoom(15);
  }, []);

  // ─── Route drawing (Step 2) ─────────────────────────────────────────────
  const _travelModeFor = (m) => {
    if (!window.google) return null;
    const tm = window.google.maps.TravelMode;
    switch (m) {
      case "walking":   return tm.WALKING;
      case "transit":   return tm.TRANSIT;
      case "bicycling": return tm.BICYCLING;
      default:          return tm.DRIVING;
    }
  };

  // Draw a route from the user's current location (`me`) to the destination
  // using Google Maps DirectionsService client-side. Populates `routeInfo`
  // for the top banner and updates `directions` for the sheet display.
  // Pins from Step 1 remain visible thanks to `suppressMarkers: true`.
  const drawRoute = useCallback((destination, chosenMode) => {
    if (!mapRef.current || !window.google) return;
    if (!destination || destination.latitude == null || destination.longitude == null) {
      toast.error("This place has no coordinates");
      return;
    }
    if (!me) {
      toast("Waiting for GPS — enable location to draw the route");
      return;
    }
    const google = window.google;
    const svc = new google.maps.DirectionsService();
    const travelMode = _travelModeFor(chosenMode || mode);

    setBusy(true);
    svc.route({
      origin: { lat: me.lat, lng: me.lng },
      destination: { lat: parseFloat(destination.latitude), lng: parseFloat(destination.longitude) },
      travelMode,
    }, (result, status) => {
      setBusy(false);
      if (status !== "OK" || !result?.routes?.length) {
        console.error("Directions failed:", status);
        toast.error(`Could not draw route (${status})`);
        return;
      }
      try {
        directionsRendererRef.current.setDirections(result);
      } catch (e) {
        console.error("Renderer failed:", e);
        return;
      }
      const leg = result.routes[0].legs[0];
      const info = {
        name: destination.name || "Destination",
        distance: leg.distance?.text || "",
        duration: leg.duration?.text || "",
        mode: (chosenMode || mode),
      };
      setRouteInfo(info);
      setDirections({
        distance_text: info.distance,
        duration_text: info.duration,
        mode: info.mode,
        destination: info.name,
      });
    });
  }, [me, mode]);

  // Legacy wrapper — keeps the existing selected-place "Get directions" flow
  // (with travel-mode chips) working. Simply forwards to drawRoute().
  const runDirections = useCallback((place, chosenMode) => {
    drawRoute(place, chosenMode);
  }, [drawRoute]);

  // ─── React to voice/text intent (state.intent from /voice/intent) OR
  //     to [MODULE:geolocation:{...}] tag (state.tag_data from VoiceProvider)
  useEffect(() => {
    const intent = loc.state?.intent;
    const tag = loc.state?.tag_data;
    if (intent?.module === "geolocation" || tag?.action) {
      const action = tag?.action || intent?.action;
      const f = tag || intent?.extracted_fields || {};
      if (action === "directions" && f.destination) {
        runSearch(f.destination, { autoSelect: true }).then(() => { /* selected auto */ });
        // Directions triggers after result via selection effect below
        setTimeout(() => {
          setResults((cur) => {
            if (cur[0]) runDirections(cur[0], f.mode || "driving");
            return cur;
          });
        }, 400);
      } else if (action === "search" || tag?.query || f.query) {
        runSearch(tag?.query || f.query || "", { autoSelect: !!tag?.autoSelect });
      } else if (tag?.place_id || (tag?.lat && tag?.lng)) {
        // Direct place payload from voice tag: skip search and drop pin
        const p = { place_id: tag.place_id, name: tag.name || "Selected place", address: tag.address, latitude: tag.lat, longitude: tag.lng };
        selectPlace(p);
        setResults([p]); setSheetOpen(true);
      }
    }
  }, [loc.state?.ts]);

  // ─── Locate-me button ──────────────────────────────────────────────────
  const locateMe = () => {
    if (!me) { toast("Waiting for GPS..."); return; }
    mapRef.current?.panTo({ lat: me.lat, lng: me.lng });
    mapRef.current?.setZoom(16);
  };

  // ─── Favorite handlers ─────────────────────────────────────────────────
  const saveFavorite = async (label, place) => {
    if (!place) { toast.error("Select a place first"); return; }
    try {
      await api.post("/geolocation/favorites", {
        action: "save", label,
        address: place.address, latitude: place.latitude, longitude: place.longitude,
      });
      toast.success(`Saved ${label} Alhamdulillah`);
      loadFavorites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save favorite");
    }
  };
  const deleteFavorite = async (id) => {
    if (!window.confirm("Remove this favorite?")) return;
    try {
      await api.post("/geolocation/favorites", { action: "delete", favorite_id: id });
      toast.success("Favorite removed");
      loadFavorites();
    } catch { toast.error("Could not delete"); }
  };
  const openFavorite = (f) => selectPlace({ name: f.label, address: f.address, latitude: f.latitude, longitude: f.longitude });

  // ─── Share handlers ────────────────────────────────────────────────────
  const shareLink = async () => {
    if (!selected) return;
    try {
      const r = await api.post("/geolocation/share", {
        action: "link", name: selected.name || "Selected",
        latitude: selected.latitude, longitude: selected.longitude, place_id: selected.place_id,
      });
      const url = r.data.share_url;
      if (navigator.share) {
        try { await navigator.share({ title: selected.name || "Location", url }); return; } catch { /* fall through */ }
      }
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
      catch { window.prompt("Copy this link:", url); }
    } catch { toast.error("Could not share"); }
  };
  const shareToCalendar = async () => {
    if (!selected) return;
    const when = window.prompt("When are you going? (e.g. 2026-07-05 18:00)", "");
    if (!when) return;
    const startIso = new Date(when.replace(" ", "T")).toISOString();
    if (isNaN(new Date(startIso).getTime())) { toast.error("Invalid date/time"); return; }
    try {
      await api.post("/geolocation/share", {
        action: "calendar", name: selected.name || "Location",
        address: selected.address, latitude: selected.latitude, longitude: selected.longitude,
        place_id: selected.place_id, start_at: startIso,
      });
      toast.success("Added to your calendar Insha'Allah");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to calendar");
    }
  };

  const homeFav = useMemo(() => favorites.find((f) => f.label === "Home"), [favorites]);
  const workFav = useMemo(() => favorites.find((f) => f.label === "Work"), [favorites]);
  const otherFavs = useMemo(() => favorites.filter((f) => f.label !== "Home" && f.label !== "Work"), [favorites]);

  return (
    <div data-testid={GEO.page} style={{ display: "flex", flexDirection: "column", gap: 10, height: "calc(100vh - 220px)", minHeight: 460, color: C.white }}>
      {/* Search + favourites row */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: 13, color: "var(--text-primary)" }} />
          <input
            data-testid={GEO.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
            placeholder="Search places, mosques, halal food..."
            style={{ ...inpStyle, paddingLeft: 34 }}
          />
        </div>
        <button data-testid={GEO.searchBtn} onClick={() => runSearch(query)} style={primaryBtn}>Search</button>
        <button data-testid={GEO.locateMe} onClick={locateMe} title="Locate me" style={iconBtn}><Locate size={16} /></button>
      </div>

      {/* Favourite chips */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button data-testid={GEO.favHome} onClick={() => homeFav ? openFavorite(homeFav) : selected && saveFavorite("Home", selected)}
          style={{ ...chipStyle, background: homeFav ? C.mosaic1 : "var(--bg-secondary)", color: homeFav ? "#ffffff" : "var(--text-primary)", border: `1px solid var(--border-color)` }}>
          <Home size={12} /> Home {homeFav ? "" : selected ? "(save)" : ""}
        </button>
        <button data-testid={GEO.favWork} onClick={() => workFav ? openFavorite(workFav) : selected && saveFavorite("Work", selected)}
          style={{ ...chipStyle, background: workFav ? C.mosaic1 : "var(--bg-secondary)", color: workFav ? "#ffffff" : "var(--text-primary)", border: `1px solid var(--border-color)` }}>
          <Briefcase size={12} /> Work {workFav ? "" : selected ? "(save)" : ""}
        </button>
        {otherFavs.slice(0, 6).map((f) => (
          <span key={f.id} data-testid={GEO.favItem} style={{ ...chipStyle, background: C.card, gap: 4 }}>
            <button onClick={() => openFavorite(f)} style={{ background: "transparent", border: 0, color: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Star size={11} /> {f.label}
            </button>
            <button data-testid={GEO.favDelete} onClick={() => deleteFavorite(f.id)} aria-label={`Delete ${f.label}`} style={{ background: "transparent", border: 0, color: C.high2, cursor: "pointer", padding: 0, marginLeft: 4 }}>
              <X size={11} />
            </button>
          </span>
        ))}
        {selected && (
          <button data-testid={GEO.favAdd} onClick={() => setShowLabelPrompt(true)} style={{ ...chipStyle, background: "transparent" }}>
            <Plus size={12} /> Save as favourite
          </button>
        )}
      </div>

      {loadError && (
        <div data-testid={GEO.errorBanner} style={{ background: C.card, border: `1px solid ${C.high2}`, borderRadius: 8, padding: "10px 14px", color: C.high2, fontSize: 12 }}>
          ⚠ {loadError}
        </div>
      )}

      {/* Map */}
      <div style={{ position: "relative", flex: 1, borderRadius: 12, overflow: "hidden", border: `1px solid ${C.mosaic1}`, background: C.card, minHeight: 300 }}>
        <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
        {!mapReady && !loadError && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.mosaic2, fontSize: 13 }}>Loading map...</div>
        )}

        {/* Route info banner (top of map) — Step 2 */}
        {routeInfo && (
          <div
            data-testid="geolocation-route-banner"
            style={{
              position: "absolute", top: 12, left: 12, right: 12, zIndex: 350,
              display: "flex", alignItems: "center", gap: 10,
              background: "#0f3d0f", border: "1px solid #CEF0CE",
              color: "#ffffff", borderRadius: 10,
              padding: "10px 14px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
              fontSize: 13,
            }}
          >
            <Route size={14} style={{ flexShrink: 0, color: "#CEF0CE" }} />
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 600 }}>{routeInfo.name}</span>
              <span style={{ color: "#CEF0CE" }}>{`: ${routeInfo.distance} · ${routeInfo.duration}`}</span>
            </div>
            <button
              data-testid="geolocation-route-clear"
              onClick={clearRoute}
              aria-label="Clear route"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: "50%",
                background: "transparent", color: "#CEF0CE", border: 0, cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Floating quick-search bubble (bottom-right of map) */}
        <div
          style={{
            position: "absolute", right: 14, bottom: 14, zIndex: 400,
            display: "flex", alignItems: "center",
          }}
        >
          {!bubbleOpen ? (
            <button
              data-testid="geo-bubble-pill"
              onClick={() => {
                setBubbleOpen(true);
                setTimeout(() => bubbleInputRef.current?.focus(), 30);
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 16px", borderRadius: 24,
                background: "var(--dnya-mosaic-1, #1a5c1a)",
                color: "var(--dnya-high-1, #CEF0CE)",
                border: 0, cursor: "pointer",
                fontSize: 13, fontWeight: 600,
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                minHeight: 40,
              }}
              title="Quick search"
            >
              <Search size={14} /> Search
            </button>
          ) : (
            <div
              data-testid="geo-bubble-expanded"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "var(--dnya-card, #0f3d0f)",
                border: "1px solid var(--dnya-high-1, #CEF0CE)",
                borderRadius: 24, padding: "6px 10px 6px 14px",
                width: "min(80vw, 260px)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
              }}
            >
              <Search size={14} style={{ color: "var(--dnya-high-1, #CEF0CE)", flexShrink: 0 }} />
              <input
                ref={bubbleInputRef}
                data-testid="geo-bubble-input"
                value={bubbleQuery}
                onChange={(e) => setBubbleQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const q = bubbleQuery.trim();
                    if (!q) return;
                    setQuery(q); runSearch(q);
                    setBubbleOpen(false); setBubbleQuery("");
                  }
                  if (e.key === "Escape") {
                    setBubbleOpen(false); setBubbleQuery("");
                  }
                }}
                placeholder="Search by name or postal code"
                style={{
                  flex: 1, minWidth: 0, background: "transparent", border: 0,
                  outline: "none", color: "var(--dnya-white, #ffffff)",
                  fontSize: 13, padding: "6px 0",
                }}
              />
              <button
                data-testid="geo-bubble-submit"
                onClick={() => {
                  const q = bubbleQuery.trim();
                  if (!q) return;
                  setQuery(q); runSearch(q);
                  setBubbleOpen(false); setBubbleQuery("");
                }}
                aria-label="Submit search"
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 30, height: 30, borderRadius: "50%",
                  background: "var(--dnya-mosaic-1, #1a5c1a)",
                  color: "var(--dnya-white, #ffffff)", border: 0, cursor: "pointer",
                }}
              >
                <Search size={13} />
              </button>
              <button
                data-testid="geo-bubble-close"
                onClick={() => { setBubbleOpen(false); setBubbleQuery(""); }}
                aria-label="Close search"
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 26, height: 26, borderRadius: "50%",
                  background: "transparent",
                  color: "var(--dnya-high-2, #DFF2DF)", border: 0, cursor: "pointer",
                }}
              >
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom sheet — results + selected detail */}
      {sheetOpen && (
        <div data-testid={GEO.bottomSheet} style={{
          position: "sticky", bottom: 0, background: C.card, border: `1px solid ${C.mosaic1}`,
          borderRadius: 12, padding: 14, maxHeight: 320, overflowY: "auto",
          boxShadow: "0 -6px 20px rgba(10,46,10,0.4)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ color: C.high1, fontSize: 13, fontWeight: 600 }}>
              {selected ? selected.name : `${results.length} result${results.length === 1 ? "" : "s"}`}
            </div>
            <button onClick={() => { setSheetOpen(false); setSelected(null); clearRoute(); }} style={iconBtn} aria-label="Close"><X size={14} /></button>
          </div>

          {!selected && results.map((p) => (
            <div
              key={p.place_id || `${p.latitude},${p.longitude}`}
              style={{
                position: "relative",
                background: "transparent",
                border: `1px solid ${C.mosaic1}`,
                borderRadius: 8,
                marginBottom: 6,
                overflow: "hidden",
              }}
            >
              <button
                data-testid={GEO.resultItem}
                onClick={() => {
                  selectPlace(p);
                  const entry = pinLookupRef.current.get(p.place_id || `${p.latitude},${p.longitude}`);
                  if (entry && mapRef.current) {
                    if (openInfoRef.current && openInfoRef.current !== entry.infoWindow) openInfoRef.current.close();
                    entry.infoWindow.open(mapRef.current, entry.marker);
                    openInfoRef.current = entry.infoWindow;
                  }
                }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 92px 10px 12px",
                  background: "transparent", color: C.white, border: 0,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: C.mosaic2, marginTop: 2 }}>
                  {p.address}
                  {p.distance_text ? ` · ${p.distance_text}` : ""}
                </div>
              </button>
              <button
                data-testid={`geolocation-row-directions-${p.place_id || p.name}`}
                onClick={(e) => { e.stopPropagation(); drawRoute(p, "driving"); }}
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: C.mosaic1, color: "#ffffff", border: 0,
                  borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 500,
                  cursor: "pointer", minHeight: 30,
                }}
                aria-label={`Directions to ${p.name}`}
              >
                <Route size={12} /> Directions
              </button>
            </div>
          ))}

          {selected && (
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.high2, fontSize: 12, marginBottom: 10 }}>
                <MapPin size={12} /> {selected.address}
              </div>

              {/* Directions row */}
              <div style={{ marginTop: 6 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                  {[["driving", GEO.modeDriving], ["walking", GEO.modeWalking], ["transit", GEO.modeTransit], ["bicycling", GEO.modeBicycling]].map(([m, tid]) => (
                    <button
                      key={m}
                      data-testid={tid}
                      onClick={() => { setMode(m); runDirections(selected, m); }}
                      style={{
                        padding: "6px 12px", borderRadius: 999, fontSize: 11, textTransform: "capitalize",
                        background: mode === m ? C.mosaic1 : "transparent",
                        color: mode === m ? C.white : C.high1,
                        border: `1px solid ${mode === m ? C.high1 : C.mosaic1}`, cursor: "pointer", minHeight: 30,
                      }}
                    >{m}</button>
                  ))}
                </div>
                <button data-testid={GEO.directionsBtn} onClick={() => runDirections(selected, mode)} style={{ ...primaryBtn, width: "100%" }} disabled={busy}>
                  <Route size={14} /> Get directions
                </button>
              </div>

              {directions && (
                <div data-testid={GEO.routeInfo} style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.mosaic1}`, fontSize: 12, color: C.high1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.white }}>{directions.distance_text}</div>
                  <div style={{ color: C.mosaic2, marginTop: 2 }}>{directions.duration_text} · {directions.mode}</div>
                </div>
              )}

              {/* Share row */}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button data-testid={GEO.shareLink} onClick={shareLink} style={{ ...ghostBtn, flex: 1 }}><Share2 size={13} /> Share link</button>
                <button data-testid={GEO.shareCalendar} onClick={shareToCalendar} style={{ ...ghostBtn, flex: 1 }}><CalendarIcon size={13} /> Add to calendar</button>
              </div>

              <button
                onClick={() => { setSelected(null); }}
                style={{ ...ghostBtn, width: "100%", marginTop: 10, borderColor: C.mosaic1, color: C.mosaic2 }}
              >
                <Navigation size={13} /> Back to results
              </button>
            </div>
          )}
        </div>
      )}

      {/* Save-as-favorite label prompt */}
      {showLabelPrompt && (
        <div onClick={() => setShowLabelPrompt(false)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.high1}`, borderRadius: 12, padding: 20, width: "min(90vw, 340px)" }}>
            <div style={{ color: C.high1, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Save as favourite</div>
            <input autoFocus value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Label (e.g. Grandma's house)" style={inpStyle} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => setShowLabelPrompt(false)} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
              <button
                onClick={async () => {
                  if (!customLabel.trim()) return toast.error("Enter a label");
                  await saveFavorite(customLabel.trim(), selected);
                  setCustomLabel(""); setShowLabelPrompt(false);
                }}
                style={{ ...primaryBtn, flex: 1 }}
              >Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared styles ──────────────────────────────────────────────────────
const inpStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, background: "var(--dnya-input-bg)", color: "var(--dnya-text)", border: `1px solid ${C.mosaic1}`, outline: "none", fontSize: 13, boxSizing: "border-box" };
const primaryBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 16px", borderRadius: 8, background: C.mosaic1, color: C.white, border: `1px solid ${C.high1}`, cursor: "pointer", fontSize: 13, fontWeight: 600, minHeight: 40 };
const ghostBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", borderRadius: 8, background: "transparent", color: C.high1, border: `1px solid ${C.high1}`, cursor: "pointer", fontSize: 12, fontWeight: 500, minHeight: 40 };
const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 8, background: "transparent", color: C.high1, border: `1px solid ${C.mosaic1}`, cursor: "pointer", padding: 0 };
const chipStyle = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, fontSize: 11, color: C.high1, border: `1px solid ${C.mosaic1}`, cursor: "pointer", minHeight: 32 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,46,10,0.55)", backdropFilter: "blur(2px)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 };
